// ─── THE DIRECTOR BLOCK ──────────────────────────────────────────────────────
//
// Appended to the LAST USER MESSAGE (depth 0) — the strongest position a
// chat-completions API offers, and the only one a small model reliably attends
// to. DeepSeek V4 Flash is 13B *active* parameters; instruction compliance
// degrades roughly as p^n, so ~120 front-loaded rules means following a random
// subset each turn. Seven rules at depth 0 beats a hundred at depth 30,000.
//
// Appending to the last message (rather than moving the rule blocks) also leaves
// the cacheable system prefix byte-identical, so DeepSeek's 0.1x prefix cache
// keeps firing.
//
// Each rule here targets a specific pain point Haley named:
//   1 → writing as the wrong character / non-English slips
//   2 → "agreeable pushover", flat sameness   (rotating objective)
//   3 → mind-reading                          (deterministic spoken-text)
//   4 → pushovers again, from the other side
//   5 → repetitive / samey replies
//   6 → replies that restate what the player just said
//   7 → replies that resolve and deflate the scene

// Rotated per turn so consecutive replies can't be driven by the same want —
// the cheapest available cure for scene-level sameness.
// NOTE: DIRECTOR_TACTICS was removed on 2026-07-28.
//
// It rotated a synthetic objective into every single reply — "produce an
// object and put it in their hands", "take them somewhere else, starting now",
// "set a condition, a deadline, or a rule". The model performed whichever one
// it was handed regardless of whether the scene called for it, and EVERY
// behavioural complaint from live play traced back to one of them: random
// gifts, constantly proposing outings, ultimatums, and demanding a promise
// before continuing. It looked arbitrary because it was arbitrary.
//
// The character's want now comes from the scene and their history, which is
// what memory, the rolling summary, character dynamics and the arc sections
// are for. Do not reintroduce a rotating objective list: if replies feel flat,
// fix the context being supplied, not by assigning the character an errand.

// ── MEASURED CLICHE LEDGER ────────────────────────────────────────────────
// Derived by frequency analysis of 900 real AI replies from this app's own
// history (2026-07-28). These are not style opinions — they are the
// constructions the models actually lean on, with counts:
//   "for a (long) moment"        164 of 900 replies (18%)
//   "…, his voice rough/low"     112 of 900 (12%)
//   "jaw tightens/ticks/works"    60 of 900
//   "the silence stretches"       53 of 900
//   "he holds her gaze"           51 occurrences
//   "hand/fingers still(s)"       35 · "tilts his head" 38 · "a beat" 36
//   "something flickers across"   20 · "goes (very) still" 16
// extractOverusedPhrases cannot catch these: it looks at ~12 recent replies
// and requires 3 hits, but an 8%-frequency tic appears about once in that
// window. Chronic tics need a standing ban; acute bursts still use the ledger.
import { voiceCheckLines } from './voiceContracts.js';
import { mostLikelyLine } from './situations.js';

const CLICHE_BAN = [
  'for a (long) moment',
  'his/her voice rough, low, quiet, or barely above a whisper',
  'holds her gaze / holds his gaze',
  'the silence stretches, settles, or sits between them',
  'jaw tightens, ticks, works, or clenches',
  'something flickers across his/her face',
  'goes very still',
  'hand/fingers still',
  'lets out a breath (soft, slow, or otherwise)',
  'tilts his/her head',
  'the corner of his/her mouth',
  'the space between them',
  'close enough that / back just enough to',
  'doesn\'t answer right away',
  // Measured inside a single live 88-reply route (James x Kya, 2026-07-28):
  // "the morning light caught the" x7, "his dark eyes fixed on" x6,
  // "a low sound/chuckle escaped" x8 openings, "the curve of her" x11,
  // "close enough that his" x7, "with an intensity that" x7.
  'the morning light caught / the light caught the',
  'his/her dark eyes fixed on',
  'a low sound escaped / a low chuckle escaped',
  'the curve of her (anything)',
  'close enough that his/her',
  'with an intensity that',
  'he said it like',
  'in a way that (made her / said)',
  // Measured across the 2026-08-30 romance-craft corpus study: one authorial
  // tic deck bleeding through five characters (lance/cruz/moros/teagan/ford).
  'tasting the shape of it / tasting the word',
  '"A pause." as its own sentence',
  'grey eyes, steady on her',
  'a sound that wasn\'t quite a laugh / not quite a smile',
  'starting consecutive replies with "He caught…"',
];

/** Pull the actually-spoken dialogue out of a message. */
export function spokenFrom(text) {
  const quotes = String(text || '').match(/[“”][^“”]{1,500}[”“]|"[^"]{1,500}"/g) || [];
  return quotes.length ? quotes.join(' ') : null;
}

// Cues that mark a quote as deliberately BELOW hearing — whispered to herself,
// under his breath, too soft to carry. The player writing these means "the
// other character does NOT hear this"; treating them as spoken-aloud made
// characters supernaturally overhear (Nicole: "the char did something softly
// and x char couldn't hear and it kept saying it heard it").
const UNHEARD_CUES = /\b(under (?:his|her|their) breath|beneath (?:his|her|their) breath|whisper(?:s|ed|ing)?|murmur(?:s|ed|ing)?|mutter(?:s|ed|ing)?|mouth(?:s|ed|ing)? (?:the words?|silently)|to (?:himself|herself|themself|themselves)|barely audible|inaudibl\w*|too (?:quiet|soft|low)(?: for| to)?|no one (?:could |can )?hear|so (?:he|she|they|nobody|no one)\s+(?:would|wouldn['’]?t|could|couldn['’]?t)\s+hear|so (?:only|no one))\b/i;

// ...unless the player EXPLICITLY says it was heard anyway. "She mutters to
// herself, though both the werewolves hear her" is the player handing the line
// to the room — supernatural hearing is a real thing in this world. An explicit
// heard-override beats any quiet cue.
const HEARD_OVERRIDES = /\b(loud enough|carr(?:y|ies|ied)|(?:though|but|except|knowing|aware)[^.!?]{0,40}\bhears?\b|\bheard\b[^.!?]{0,20}\b(?:anyway|it|her|him|them)\b|(?:both|all|everyone|they)[^.!?]{0,30}\bhears?\b|meant (?:for|to be heard)|didn['’]?t care who heard)\b/i;

// A quiet line DIRECTED AT the listener is heard by definition. Whispering in
// someone's ear is the most intimate way to be heard, not a way to be missed —
// and "'I love you,' she says softly" is a romance app's core traffic, not an
// edge case. Before this, three of four realistic soft-speech forms were
// classified unheard and the character was explicitly told the words were
// never said (audit #5).
//
// Deliberately NOT matched: a third party ("she whispers to Cruz", "whispers
// to the others") — those stay unheard, which is the behaviour Nicole
// originally asked for.
const DIRECTED_AT_LISTENER = new RegExp(
  [
    // ...in/into/against his ear, close to her ear, near their ear
    '\\b(?:in|into|against|near|close to|by)\\s+(?:his|her|their|your)\\s+ear\\b',
    // whispers/murmurs/says ... to him/her/them/you  (a pronoun = the listener)
    '\\b(?:whisper|murmur|mutter|breathe|say|said|says|tell|told|speak|spoke)\w*\\s+(?:[^.!?]{0,30}?\\s)?to\\s+(?:him|her|them|you)\\b',
    // against his mouth / lips / skin / neck / throat — spoken while touching
    '\\bagainst\\s+(?:his|her|their|your)\\s+(?:mouth|lips|skin|neck|throat|jaw|temple|hair|cheek)\\b',
    // only he/she/they could hear  → heard BY the listener
    '\\bonly\\s+(?:he|she|they|him|her|them|you)\\s+(?:could|can)\\s+hear\\b',
  ].join('|'),
  'i'
);

// Volume corrections introduce the quote that FOLLOWS them, so they are only
// consulted in the before-window.
const VOLUME_CORRECTION = /\b(?:louder|clearly|raise[sd]?\s+(?:his|her|their)\s+voice|out loud|aloud)\b/i;

/**
 * Split a player message's quotes into what the other character HEARD vs what
 * was said below hearing. A quote is unheard when an audibility cue appears in
 * the ~90 chars around it.
 */
export function splitAudibility(text) {
  const s = String(text || '');
  const re = /[“”][^“”]{1,500}[”“]|"[^"]{1,500}"/g;
  // Collect quotes with positions first, so each quote's cue window is bounded
  // by its NEIGHBOURING quotes — a "whispered" attached to quote 1 must not
  // bleed into quote 2's window ("...whispered, 'x'. Then louder: 'y'").
  const found = [];
  let m;
  while ((m = re.exec(s)) !== null) found.push({ q: m[0], start: m.index, end: m.index + m[0].length });
  const heard = [];
  const unheard = [];
  for (let i = 0; i < found.length; i++) {
    const prevEnd = i > 0 ? found[i - 1].end : 0;
    const nextStart = i < found.length - 1 ? found[i + 1].start : s.length;
    const before = s.slice(Math.max(prevEnd, found[i].start - 90), found[i].start);
    let after = s.slice(found[i].end, Math.min(nextStart, found[i].end + 90));
    // Stop at the first sentence terminator: the tag that modifies THIS quote
    // lives in the same sentence. Anything past it describes a later beat.
    const sentEnd = after.search(/[.!?]/);
    if (sentEnd !== -1) after = after.slice(0, sentEnd + 1);
    const window = before + ' ' + after;
    const quiet = UNHEARD_CUES.test(before) || UNHEARD_CUES.test(after);
    // An explicit "they hear it anyway", or a line aimed AT the listener,
    // beats the quiet cue.
    const overridden = HEARD_OVERRIDES.test(window)
      || DIRECTED_AT_LISTENER.test(window)
      || VOLUME_CORRECTION.test(before);
    if (quiet && !overridden) unheard.push(found[i].q);
    else heard.push(found[i].q);
  }
  return { heard, unheard };
}

// ── PERCEPTION: what is plainly VISIBLE vs what is genuinely PRIVATE ───────
//
// The director used to tell a character that everything outside quotation
// marks "was narration or private thought — you cannot know it." That is true
// of thoughts and false of nearly everything else. A player writing
//   "She limps in, favouring her left leg, mascara streaked down her face."
// was writing something unmissable, and the character was instructed it could
// not know any of it — and might "read the body language WRONG". That is the
// root of "characters don't notice obvious things".
//
// So: split narration into what a person in the room would obviously observe
// (actions, appearance, physical state, objects, environment) and what lives
// only in the other person's head (thoughts, memories, intentions, unspoken
// feelings). The first gets handed to the character as perception. The second
// stays author-level steering.
const INTERNAL_MARKERS = new RegExp([
  // explicit thought/feeling verbs about the SUBJECT's interior
  '\\b(?:think|thinks|thinking|thought|wonder|wonders|wondering|wondered',
  '|realis\\w+|realiz\\w+|remember\\w*|recall\\w*|reflect\\w*|muse\\w*',
  '|hope|hopes|hoping|hoped|wish|wishes|wishing|wished|fear|fears|fearing|feared',
  '|want|wants|wanting|wanted|need|needs|needed|decide|decides|deciding|decided',
  '|consider|considers|considering|considered|imagine\\w*|assume\\w*|suspect\\w*',
  '|know|knows|knowing|knew|understand\\w*|believe\\w*|doubt\\w*|regret\\w*',
  '|feel|feels|feeling|felt)\\b',
  // interior nouns
  '|\\b(?:her|his|their|the)\\s+(?:mind|thoughts?|memory|memories|heart\\s+(?:told|knew))\\b',
  '|\\b(?:to\\s+(?:herself|himself|themselves|themself))\\b',
  '|\\b(?:inwardly|internally|privately|silently\\s+(?:hoped|wished|cursed))\\b',
].join(''), 'i');

// Physical tells that are unmistakable even when a thought-verb is nearby
// ("she felt tears run down her face" — the tears are VISIBLE).
const VISIBLE_OVERRIDE = /\b(?:tears?|crying|cried|sobb\w*|shaking|trembl\w*|blood|bleed\w*|bruis\w*|limp\w*|winc\w*|flinch\w*|blush\w*|flush\w*|pale|sweat\w*|scars?|bandag\w*|wound\w*|vomit\w*|collaps\w*|stumbl\w*|faint\w*|scream\w*|shout\w*|laugh\w*|smil\w*|grin\w*|frown\w*|glar\w*|stare\w*|nods?|nodding|shrug\w*|steps?|walks?|runs?|sits?|stands?|reaches?|grabs?|holds?|carries|wearing|dressed|holding|clutch\w*)\b/i;

// Concrete, observable action — the second half of the visibility gate.
// VISIBLE_OVERRIDE covers states and injuries; this covers movement and
// handling, so ordinary RP staging ("she crosses her arms", "he sets the
// carton down") still reaches the other character as fact.
const PHYSICAL_ACTION = /\b(?:cross\w*|fold\w*|lean\w*|turn\w*|move\w*|push\w*|pull\w*|open\w*|close\w*|shut\w*|lift\w*|lower\w*|drop\w*|set\w*|place\w*|hand\w*|offer\w*|take\w*|took|toss\w*|throw\w*|threw|point\w*|gestur\w*|touch\w*|press\w*|kiss\w*|hug\w*|pull\w*|slap\w*|punch\w*|kick\w*|bite|bit|slid\w*|slide\w*|sat|stood|knelt|kneel\w*|crouch\w*|approach\w*|back\w* away|steps?|stepp\w*|enters?|entered|exits?|left the|arriv\w*|follows?|followed)\b/i;

/**
 * Split a player message's NON-QUOTED narration into what the other character
 * can obviously perceive vs what is private to the writer's character.
 * Quotes are handled separately by splitAudibility().
 */
export function splitPerception(text) {
  const s = String(text || '');
  // Remove quoted dialogue — audibility owns that.
  const narration = s.replace(/[\u201C\u201D][^\u201C\u201D]{1,500}[\u201D\u201C]|"[^"]{1,500}"/g, ' ');
  // Italicised passages are the app's convention for private thought.
  const visible = [];
  const internal = [];
  const italics = narration.match(/\*[^*]{2,400}\*/g) || [];
  for (const it of italics) internal.push(it.replace(/^\*|\*$/g, '').trim());
  const rest = narration.replace(/\*[^*]{2,400}\*/g, ' ');

  // A fragment that is nothing but a speech tag once the quote is removed.
  const BARE_TAG = /^(?:[,.\s]*)(?:he|she|they|[A-Z][a-z]+)\s+(?:say|says|said|ask|asks|asked|reply|replies|replied|add|adds|added|murmur\w*|whisper\w*|mutter\w*|breathe\w*|answer\w*|continue\w*)\b[^.!?]{0,24}[.!?]?$/i;

  for (const raw of rest.split(/(?<=[.!?])\s+|\n+/)) {
    const clause = raw.trim().replace(/\s+/g, ' ').replace(/^[,;:]\s*/, '');
    if (clause.length < 3) continue;
    if (BARE_TAG.test(clause)) continue;
    // VISIBILITY MUST BE EARNED, NOT ASSUMED.
    //
    // This used to default every unrecognised clause to VISIBLE and only move
    // it to internal on an explicit thought verb (think / know / feel / wonder).
    // Narration that judges another character carries none of those, so it all
    // landed in "you can plainly SEE this and must not ignore it".
    //
    // Live 2026-08-02: Nicole's private narration — "He was notorious. A
    // fucking serial killer who enjoyed the kill. He enjoyed the look in
    // people's eyes as they draw their last breath." — came back with
    // internal:[] and ALL of it in visible. The director then told Lance to
    // acknowledge the most striking one, and he answered a thought she never
    // said aloud: "You're not wrong... I enjoy killing. It's what I do."
    //
    // The two errors are not symmetrical. Calling something internal when it
    // was visible costs a missed reaction; calling something visible when it
    // was private is mind-reading, which is the failure players actually
    // report. So a clause is VISIBLE only when it shows a concrete physical
    // cue, and everything else is private by default.
    if (INTERNAL_MARKERS.test(clause) && !VISIBLE_OVERRIDE.test(clause)) internal.push(clause);
    else if (VISIBLE_OVERRIDE.test(clause) || PHYSICAL_ACTION.test(clause)) visible.push(clause);
    else internal.push(clause);
  }
  return { visible, internal };
}

// Overused-phrase detection (the anti-repetition ledger, lite). "Hands in his
// pockets" appeared in SIX consecutive Nolan replies; "He stops walking" opened
// three. Frequency penalties operate on tokens; this names the exact phrases at
// depth 0. Counts 2–4-word n-grams across the character's recent replies and
// returns the ones used in 3+ separate replies.
const NGRAM_STOP = new Set(['the','a','an','and','or','but','of','to','in','on','at','it','is','was','be','he','she','his','her','him','they','their','that','this','with','for','as','like','into','from']);

// ─── SINGLE-WORD MANNERISM TICS ─────────────────────────────────────────────
// Phrase frequency structurally UNDERCOUNTS a word-level tic. Measured on six
// consecutive live Cruz replies (2026-08-02): "grin" appeared in all six, but
// spread across five different constructions — "flashes her a grin", "a slow
// grin spreading", "a grin that's all easy confidence", "grin fading into",
// "His grin widens" — so no single n-gram ever accumulated enough count to win
// a slot, and the ban list came back without it. His own profile says in
// capitals that he does NOT grin (measured 12 times in 6,420 archive passages,
// against 139 smiles); the model read past it, because a prose note inside a
// descriptive field loses to the model's own prior every time.
//
// DELIBERATELY A CLOSED LIST. Counting every repeated content word would ban
// ordinary scene nouns — "eyes", "voice", "hand", "door" recur constantly in
// any RP prose and telling a character to stop writing "eyes" is worse than
// saying nothing. These are specifically MANNERISM words: the gesture
// vocabulary a model reaches for when it is padding, which is exactly where
// invented tics come from.
const TIC_WORDS = new Set([
  'grin', 'grins', 'grinning', 'smirk', 'smirks', 'smirking', 'chuckle', 'chuckles',
  'chuckling', 'sigh', 'sighs', 'sighing', 'shrug', 'shrugs', 'shrugging',
  'huff', 'huffs', 'huffing', 'scoff', 'scoffs', 'scoffing', 'growl', 'growls',
  'growling', 'purr', 'purrs', 'purring', 'smile', 'smiles', 'smiling',
  'laugh', 'laughs', 'laughing', 'quirk', 'quirks', 'tilt', 'tilts', 'tilting',
  'flick', 'flicks', 'flicking', 'twitch', 'twitches', 'twitching',
  'lingers', 'lingering', 'exhale', 'exhales', 'breathes', 'murmur', 'murmurs',
]);

// ─── STRUCTURAL FORMULAS ────────────────────────────────────────────────────
// Some tics are not a word or a phrase but a SHAPE, so neither the n-gram pass
// nor the tic-word pass can see them. The clearest is the closing private
// realisation, which ran through 4 of 8 consecutive Cruz replies and 1 of 4
// Lance replies on 2026-08-02: "She's not just fixing paperwork. She's building
// a home." / "She's not Lachesis. She's not the goddess who haunted his dreams."
//
// Cruz's profile ALREADY banned the literal tag "the thought settles in his
// chest". The model's response was to keep the formula and rename it — Lance
// closed with "the realization settles in his chest" and "the thought burns,
// sharp and unexpected". Banning literal wording just moves the slop one
// synonym over; the pattern has to be named as a pattern.
//
// `min` is per-pattern on purpose. Stock LLM slop earns a ban the second time
// it appears; ordinary prose constructions ("warm and easy") are only a tic
// when they are in half the replies, and banning those too eagerly would push a
// character off perfectly good writing.
const FORMULA_PATTERNS = [
  { label: 'closing with a private realisation of the "X is not just A — X is B" shape',
    re: /\b(?:she|he|they)'s not (?:just |only |)[^.!?]{1,70}[.!?]\s*(?:but )?(?:she|he|they)'s\b/i, min: 2 },
  { label: 'ending on a "the thought/realisation settles…" tag (in any wording)',
    re: /\b(?:thought|realisation|realization)\s+(?:settles|burns|lands|sits)\b/i, min: 2 },
  { label: 'stacked negation fragments ("Not from fear. Not from cold.")',
    re: /\bnot (?:from|of|because) [^.!?]{1,30}[.!?]\s*(?:not|of)\b/i, min: 2 },
  { label: 'the "eyes flick to X, then back to her" beat',
    re: /\beyes flick(?:s|ing)?\b[^.!?]{0,40}\bback to (?:her|him|them)\b/i, min: 2 },
  { label: 'pairing an adjective with "and" ("warm and easy", "low and soft")',
    re: /\b(?:warm|low|soft|rich|slow)\s+and\s+\w+/i, min: 0 },
  { label: 'leaning back with an arm slung over something',
    re: /\bleans? back\b/i, min: 0 },
  // The muted-affect register (2026-08-30 study): the model qualifying every
  // feeling into the middle instead of feeling it at full value.
  { label: 'qualifying every feeling into the middle — "not quite a laugh", "something between X and Y" — name the feeling at FULL VALUE instead',
    re: /\bnot quite (?:a |an |)\w+|\bsomething between\b|\bwasn'?t quite\b/i, min: 2 },
  { label: "opening by quoting the player's own phrase back before answering it — open on your reaction, your move, or them, never their words",
    re: /^\s*["\u201c][^"\u201d]{2,45}\?["\u201d]/, min: 2 },
  { label: 'the narrated sound-that-wasn\'t ("a low sound escaped him") — if it was a laugh, write the laugh',
    re: /\b(?:a|the)\s+(?:low\s+|small\s+|rough\s+|quiet\s+)?sound\s+(?:escaped|left|slipped)\b/i, min: 2 },
];

/**
 * Structural formulas repeated across recent replies, named in plain language.
 * `min: 0` means "use the half-of-replies rule" rather than a fixed count.
 */
export function overusedFormulas(texts) {
  const half = Math.max(2, Math.ceil(texts.length / 2));
  return FORMULA_PATTERNS
    .map(p => ({ p, n: texts.filter(t => p.re.test(String(t))).length }))
    .filter(({ p, n }) => n >= (p.min || half))
    .sort((a, b) => b.n - a.n)
    .map(({ p }) => p.label)
    .slice(0, 3);   // same reasoning as the ban list — the top three, not all six
}

/**
 * Mannerism words this character has leaned on across their recent replies.
 * Presence-per-reply, not raw count: three grins in one paragraph is a bad
 * paragraph, but a grin in every reply is a TIC, and the tic is what needs
 * naming. Threshold is "at least half the replies, and at least twice".
 */
function overusedTicWords(texts) {
  const seen = {};
  for (const t of texts) {
    const words = new Set(String(t).toLowerCase().replace(/[^a-z'\s-]/g, ' ').split(/\s+/).filter(Boolean));
    for (const w of words) if (TIC_WORDS.has(w)) seen[w] = (seen[w] || 0) + 1;
  }
  const floor = Math.max(2, Math.ceil(texts.length / 2));
  return Object.entries(seen)
    .filter(([, c]) => c >= floor)
    .sort((a, b) => b[1] - a[1])
    .map(([w]) => w);
}
// WINDOW 10 → 30 (2026-10-12). Haley caught a gesture reused eleven replies
// apart ("This was repeated before"): the ban only ever looked at the last ten
// of the character's own replies, so anything older came back clean. Thirty
// covers a whole evening of play; the list is still capped at six.
export const REPEAT_WINDOW = 30;
export function extractOverusedPhrases(recentOwnTexts, max = 6) {
  const texts = (recentOwnTexts || []).filter(Boolean).slice(-REPEAT_WINDOW);
  if (texts.length < 2) return [];
  const perReply = texts.map(t => {
    const words = String(t).toLowerCase().replace(/[^a-z'\s-]/g, ' ').split(/\s+/).filter(Boolean);
    const grams = new Set();
    for (let n = 2; n <= 4; n++) {
      for (let i = 0; i + n <= words.length; i++) {
        const g = words.slice(i, i + n);
        // A phrase must contain at least one non-stopword to be meaningful.
        if (g.every(w => NGRAM_STOP.has(w))) continue;
        grams.add(g.join(' '));
      }
    }
    return grams;
  });
  const counts = {};
  for (const grams of perReply) for (const g of grams) counts[g] = (counts[g] || 0) + 1;
  // FREQUENCY BEFORE LENGTH. Sorting by length first meant a 4-gram used twice
  // outranked a phrase used in every single reply — so "a grin", which appeared
  // in all four of Cruz's consecutive replies, lost its slot to fragments of one
  // long sentence used twice. A phrase repeated more often IS more of a tic,
  // regardless of how many words it happens to be.
  //
  // Also drop phrases that are mostly filler: with overlap-collapsing freeing
  // slots, low-content pairs like "out a" and "warm and" started surfacing and
  // banning them tells the model nothing useful.
  const contentful = ([g]) => {
    const w = g.split(' ');
    return w.filter(x => !NGRAM_STOP.has(x)).length / w.length >= 0.5;
  };
  const hits = Object.entries(counts)
    .filter(([_g, c]) => c >= 2)   // 3 was so strict it effectively never fired
    .filter(contentful)
    .sort((a, b) => (b[1] - a[1]) || (b[0].split(' ').length - a[0].split(' ').length));
  // Prefer longer phrases, and collapse OVERLAPPING windows of the same
  // sentence — not just containment.
  //
  // The containment-only test let sliding windows of one phrase eat the whole
  // ban list. Measured on four consecutive live Cruz replies, the six slots
  // came back as: "the thought settles in", "thought settles in his",
  // "settles in his chest", "in his chest warm", "his chest warm and",
  // "he leans back". Five of six are fragments of ONE sentence, because
  // "the thought settles in" does not *contain* "thought settles in his".
  // Meanwhile "a grin" — which appeared in all four replies and is exactly
  // what Nicole was seeing ("repeating responses in nearly every channel") —
  // never got a slot. Nicole's report was structural repetition, and the one
  // mechanism meant to catch it was full of duplicates of a single phrase.
  //
  // Two phrases now count as the same when either contains the other OR they
  // share a 2-word window, which is what overlapping n-grams always do.
  const picked = [];
  const windows = (g) => {
    const w = g.split(' ');
    const out = new Set();
    for (let i = 0; i + 2 <= w.length; i++) out.add(w[i] + ' ' + w[i + 1]);
    return out;
  };
  const pickedWindows = [];
  for (const [g] of hits) {
    if (picked.some(p => p.includes(g) || g.includes(p))) continue;
    const gw = windows(g);
    if (pickedWindows.some(pw => [...gw].some(x => pw.has(x)))) continue;
    picked.push(g);
    pickedWindows.push(gw);
    if (picked.length >= max) break;
  }
  // Tic words go FIRST. They are the strongest signal we have — a mannerism in
  // half the replies is more of a tell than any phrase — and putting them at the
  // front of the ban list means they survive the model's attention falling off
  // the end of a long enumeration. Anything already covered by a picked phrase
  // is dropped so the list does not say the same thing twice.
  // Tic words take the FRONT slots but do not enlarge the list — `max` is the
  // total. A ban list that grows without bound is self-defeating: rule 5 is
  // read by a 24B model, and an enumeration long enough to dilute the other six
  // rules costs more than the marginal ban is worth.
  const tics = overusedTicWords(texts).filter(w => !picked.some(p => p.includes(w)));
  return [...tics, ...picked].slice(0, max);
}

// ─── REPEATED TOPICS — the CONTENT repeat, not the wording repeat ───────────
//
// Nicole, live 2026-08-04: "I notice it repeats a bit. Like always mentioning
// certain things. Like how long Lance has been around for. Or like the fact
// that he murders werewolves but doesn't wanna murder her. Those repeat pretty
// often."
//
// Every anti-repetition mechanism this file already has works on WORDING or
// SHAPE — n-grams, tic words, structural formulas, five-gram overlap, sentence
// reuse. A character who re-asserts the same FACT in freshly written prose
// every single reply defeats all of them: "three hundred years of this",
// "I have outlived everyone you know", "I was old before your grandmother was
// born" share almost no tokens, and are the same beat three times.
//
// So this counts TOPICS instead of phrases. A content word (crudely stemmed,
// so kill/kills/killed/killing collapse together) that appears in most of the
// character's recent replies is something they keep bringing up, whatever
// words they dressed it in that turn.
//
// CURATED FAMILIES ONLY — an open-ended "what content words recur" pass was
// built first, measured against real play, and DELETED. Run over 24
// consecutive live Lance replies from the Pack House channel (2026-08-04/05,
// the exact stretch Nicole was reading when she reported this), it fired on
// 19 of 19 six-reply windows and the things it named were:
//   "raven"      — his pet name for her character. Recurs because he is fond.
//   "shadow"     — his actual magic. Recurs because it is what he IS.
//   "firelight", "treeline", "blade", "coat" — this scene's furniture.
//   "that's", "don't", "she's", "i've" — contractions.
// Telling a character to stop saying "raven" or "shadow" would be worse than
// the problem it was meant to solve, and there is no lexical signal that
// separates those from a repeated claim. So the discovery pass is gone and
// what remains is a hand-checked list of SELF-ASSERTIONS: the things a
// character keeps re-introducing themselves with. Both of Nicole's examples
// are of exactly that kind, and so is every complaint of this class so far.
//
// Vocabulary is deliberately tight. 'blood' was measured into the killing
// family and taken back out: in a world of vampires and werewolves it is
// ordinary scene vocabulary (13/24 replies) rather than a claim being
// restated. Same reasoning for a bare 'old'.
const TOPIC_FAMILIES = [
  // Haley, 2026-10-06: "alistair also keeps repeating 2000 years every like
  // few responses". A fact about yourself is a tic at TWO of six, not three:
  // the listener already knows it the second time. `floor: 2` lowers the
  // half-of-replies rule for this family only.
  { topic: 'how long you have lived / how old you are',
    floor: 2,
    words: ['year', 'years', 'century', 'centuries', 'decade', 'decades',
            'millennia', 'millennium', 'thousand', 'eons', 'aeons',
            'immortal', 'immortality', 'lifetime', 'lifetimes', 'eternity',
            'eternal', 'outlive', 'outlived', 'outlives', 'ancient'] },
  { topic: 'that you kill / what you have killed',
    words: ['kill', 'kills', 'killed', 'killing', 'murder', 'murders',
            'murdered', 'murdering', 'slaughter', 'slaughtered', 'butcher',
            'butchered', 'corpse', 'corpses'] },
  { topic: 'what you are — the monster, and the danger you pose to them',
    words: ['monster', 'monsters', 'beast', 'predator', 'prey', 'danger',
            'dangerous', 'threat'] },
  { topic: 'your restraint — that you are choosing not to act on it',
    words: ['restraint', 'resist', 'resisted', 'resisting', 'spare', 'spared',
            'sparing'] },
];

/**
 * Topics the character has brought up in MOST of their recent replies.
 * Returns plain-language labels, not literal phrases — the point is the
 * subject, not the wording (which the ban list already covers).
 * Exported for tests.
 */
// THE SAME BEAT, TWICE, IN DIFFERENT WORDS.
//
// Haley, 2026-08-11, two consecutive Cruz replies. First: "I saw that." /
// "Right there." / "That's my wolf in her, looking out at me" / voice low.
// Second: "I saw it. Right there in your eyes." / "There she is. My wolf,
// looking back at me through her eyes." / voice low. He noticed her wolf,
// remarked on it and touched her face — then did all three again.
//
// NEITHER existing guard sees this, and both are right not to:
//   isNearDuplicate needs verbatim reassembly. Measured on this pair, 5-gram
//     overlap is 0.006 against a 0.25 threshold — they share almost no word
//     sequences at all. It was built for the 2026-08-02 Lance incident, where
//     whole sentences were lifted and reordered (overlap 0.49).
//   repeatedTopics needs a RUN — 3 of the last 6 replies. This is two in a row.
//
// Content words separate it cleanly. Measured on that pair against a genuinely
// different beat from the same scene: 0.195 for the repeat, 0.015 and 0.000
// for the others. A floor of 0.10 sits in the middle of a 13x gap.
const BEAT_STOPWORDS = new Set(('a an and the his her he she it its of to in on at with that this is was ' +
  'as but for so just not no into once back down up out him them they there here what when then like ' +
  'from over under again still even only very much more most been being had has have who whom whose ' +
  'about against between through during before after above below').split(' '));

const beatBag = (text) => new Set(
  String(text || '').toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/)
    .filter(w => w.length > 3 && !BEAT_STOPWORDS.has(w))
);

/**
 * Did the character just write the same beat twice? Returns the shared words
 * that make it the same beat, or [] when it did not.
 */
export function echoedLastBeat(recentOwnTexts, { floor = 0.10 } = {}) {
  const texts = (recentOwnTexts || []).filter(t => String(t || '').trim()).slice(-2);
  if (texts.length < 2) return [];
  const a = beatBag(texts[0]);
  const b = beatBag(texts[1]);
  if (a.size < 5 || b.size < 5) return [];
  const shared = [...b].filter(w => a.has(w));
  const union = new Set([...a, ...b]).size;
  if (!union || shared.length / union < floor) return [];
  return shared.slice(0, 8);
}

export function repeatedTopics(recentOwnTexts, { playerText = '', max = 3 } = {}) {
  const texts = (recentOwnTexts || []).filter(Boolean).slice(-6);
  if (texts.length < 3) return [];        // no evidence of a pattern yet

  // NEVER shush a topic the PLAYER is driving. Measured on the same live
  // stretch: across Nicole's own messages in that scene she raises killing
  // herself in most of them — they are planning to go kill rogues together,
  // so it is the live plot, not his tic. She never once raises how old he is;
  // that one is entirely his. Telling him to drop the subject she just asked
  // about would be the app fighting its own player, which is the failure this
  // codebase has paid for before.
  const asked = new Set(
    String(playerText || '').toLowerCase().replace(/[^a-z\s'-]/g, ' ').split(/\s+/).filter(Boolean)
  );

  // HALF THE RECENT REPLIES, calibrated on live play rather than guessed. Over
  // the same 24 real Lance replies, a floor of 3-of-6 fires on 4 of 19 windows
  // — silent through the ordinary stretch and firing exactly across the run
  // Nicole was reading (age in 4 of 6 replies there, killing in 5 of 6,
  // monster/danger in 3 of 6). A floor of 4-of-6 caught only the last two
  // windows and missed most of what she was seeing.
  const floor = Math.max(3, Math.ceil(texts.length * 0.5));

  const perReplyWords = texts.map(t => new Set(
    String(t).toLowerCase().replace(/[^a-z\s'-]/g, ' ').split(/\s+/).filter(Boolean)
  ));

  return TOPIC_FAMILIES
    .filter(fam => !fam.words.some(w => asked.has(w)))   // the player raised it — leave it alone
    .map(fam => {
      const words = new Set(fam.words);
      const n = perReplyWords.filter(set => [...set].some(w => words.has(w))).length;
      return { label: fam.topic, n, floor: fam.floor ? Math.min(fam.floor, floor) : floor };
    })
    .filter(h => h.n >= h.floor)
    .sort((a, b) => b.n - a.n)
    .slice(0, max)
    .map(h => h.label);
}

// ─── CONSTRUCTION NUDGE (craft §2, 2026-08-04) ──────────────────────────────
// The craft rules already say "vary your construction" as a standing
// principle; this makes it ACUTE when the evidence shows the model has
// stopped listening. Two shapes players actually notice: consecutive replies
// that all open the same way, and consecutive replies that all land on a
// question. Per-turn text (the director block is appended to the last user
// message), so it never touches the cached prefix.
function openingShapeOf(text) {
  const s = String(text || '').trim();
  if (!s) return null;
  if (/^["“]/.test(s)) return 'with dialogue';
  if (/^\*/.test(s)) return 'with an italicized beat';
  const w = (s.toLowerCase().match(/^[a-z']+/) || [])[0];
  return w ? `with "${w[0].toUpperCase()}${w.slice(1)}…"` : null;
}

function endsOnQuestion(text) {
  // Trailing quotes/asterisks don't change what the last sentence IS.
  const s = String(text || '').trim().replace(/["”*’')\]]+$/g, '').trim();
  return /\?$/.test(s);
}

/**
 * One evidence-based line when the last three replies share a construction,
 * or '' when they don't. Exported for tests.
 */
export function constructionNudge(recentOwnTexts) {
  const texts = (recentOwnTexts || []).filter(Boolean).slice(-3);
  if (texts.length < 3) return '';
  const parts = [];
  const shapes = texts.map(openingShapeOf);
  if (shapes[0] && shapes.every(s => s === shapes[0])) {
    parts.push(`Your last three replies all opened the same way (${shapes[0]}) — open this one differently.`);
  }
  if (texts.every(endsOnQuestion)) {
    parts.push(`Your last three replies each ended on a question — land this one on something other than a question.`);
  }
  return parts.join(' ');
}

// ─── ENSEMBLE DECISION ──────────────────────────────────────────────────────
// Should a second character join this turn? Live failure: "Haley narrows her
// eyes at him... her face next to his" — an intimate one-on-one beat — got
// "Nolan & Audrey is typing" because mere PRESENCE gave a 45% ensemble roll.
// Deterministic-first now:
//   • message clearly directed at ONE person (singular pronouns, no group cue,
//     no other character named) → NEVER ensemble
//   • another character explicitly named → almost always ensemble
//   • otherwise, only when BOTH characters were genuinely active in the recent
//     exchange (a real group scene), and then only sometimes.
export function isDirectedAtOne(text) {
  const s = String(text || '');
  const singular = /\b(him|his|he|she|her|hers)\b/i.test(s);
  const group = /\b(both|everyone|they|them|these two|you two|you all|y'all|guys|all of you)\b/i.test(s);
  return singular && !group;
}

export function shouldEnsemble({ text, hintedOther, bothActiveRecently, rand = Math.random() }) {
  if (hintedOther) return rand < 0.9;               // player named someone else — bring them in
  if (isDirectedAtOne(text)) return false;          // intimate/directed beat — one voice only
  if (!bothActiveRecently) return false;            // not a live group scene — don't invent one
  // Haley, 2026-08-04: "it's okay to answer as more than one character if
  // it's good story." A genuine group scene now USUALLY answers as a group;
  // the directed-at-one guard above still protects intimate beats.
  return rand < 0.7;
}

// ─── UNMARKED OOC BANTER ────────────────────────────────────────────────────
//
// Haley, 2026-08-02, live: Cruz's "language is a little weird and off track,
// it's like he's not really responding to my response direct and not
// addressing what I write to him."
//
// Root cause: `channel_messages` has NO is_ooc column, and ChannelView had no
// OOC detection at all. The co-op channel carries both the scene AND the two
// writers talking to each other about the scene, in the same stream. Every
// "LOL yeah", "this'll be interesting", "Idk what it's going to be like for
// cruz at all" was handed to the model as `[Haley]: <text>` in-scene dialogue,
// and the DIRECTOR then said, verbatim, "Cruz heard these words out loud:
// Idk what it's going to be like for cruz at all." He WAS addressing what she
// wrote. She had written banter.
//
// extractOOC only catches EXPLICIT markers — ((…)), [OOC: …], "ooc:". Nobody
// marks casual chatter, so none of it was caught.
//
// A large model shrugs this off and finds the last real in-character beat; a
// 24B model answers the literal last line. That is why it shows up on
// mistral-small and not on hermes-4-70b.
//
// BIAS, deliberately asymmetric: only classify as OOC on POSITIVE evidence,
// and never when in-character markers are present. A missed OOC line is
// exactly today's behaviour and costs nothing new. A misclassified RP line
// would hide real story from the model, which is strictly worse.
// Laughter. Must tolerate the real spellings: "LOLOL", "LMAOO",
// "hahahahahhaa". An earlier \b-anchored version missed that last one because
// the trailing run has no word boundary, and it cost four true positives.
// Laughter. Must tolerate "LOLOL", "LMAOO", "hahahahahhaa" — and must NOT
// match ordinary prose. An earlier version used h(?:a|e){2,}, which matches
// "head", "heart" and "hear": running it over the real Pack House channel
// classified 29 of 134 messages as banter, almost all of them roleplay, on the
// strength of "shakes her head". The truncated fixtures it was written against
// happened to contain none of those words. Substring signals that look
// rigorous and are fiction — the exact trap the verdict instructions warn
// about — which is why this is anchored to whole laughter tokens.
const LAUGH = /\b(?:l(?:ol)+|lmf?ao+|rofl)\b|\b(?:ha|he){2,}[ha]*\b|xD(?![a-z])|🤣|😂|😭/i;
const WRITER_META = new RegExp(
  '\\b(' +
  'ooc|irl|in rl|in real life|my bad|idk|imo|tbh|fr fr|' +          // chat shorthand
  'this route|that route|the route|routes|rp\'?d?|roleplay|' +      // the craft
  'my char|your char|our char|the chapter|the arc|the plot|' +
  'headcanon|retcon|timeline|' +
  'gonna be interesting|this\'?ll be interesting' +
  ')\\b', 'i');
// The writers talking ABOUT the cast, in the third person, with no scene around
// it — "Cruz would forgive", "Ryder would be good for you in rl".
const WRITER_VOICE = /\b(i (?:think|guess|swear|wanted|enjoy|should have|genuinely|never thought|can'?t see|hope)|you love|we can'?t|i'?ll (?:watch|get)|good to know|the irony is|it'?s funny)\b/i;

// In-character markers. Scene text wins over a thin OOC score.
const HAS_DIALOGUE = /"[^"]{6,}"|“[^”]{6,}”/;          // quoted speech
const HAS_ACTION   = /\*[^*]{4,}\*/;                    // *she leans in*
// Third-person narration: a pronoun followed by a narrative ACTION verb.
//
// Bare copulas (was/were/had) were in this list and were the single biggest
// source of misclassification — "she was a mate's best friend" and "my Char
// did text your Char she was staying somewhere" are both chat, and both were
// being read as prose because of "she was". Casual speech is full of
// pronoun+copula; roleplay narration is characterised by ACTION.
const HAS_NARRATION = /\b(she|he|they|the\s+\w+)\s+(?:\w+\s+)?(pauses?|paused|leans?|leaned|smiles?|smiled|looks?|looked|turns?|turned|walks?|walked|waits?|waited|squeezes?|squeezed|bites?|bit|opens?|opened|murmurs?|murmured|whispers?|whispered|steps?|stepped|reaches?|reached|watches?|watched|stands?|stood|sits?|sat|nods?|nodded|shrugs?|shrugged|grins?|grinned|sighs?|sighed|laughs?|laughed|moves?|moved|pulls?|pulled|takes?|took|says?|said|flinches?|flinched|freezes?|froze|stares?|stared|blinks?|blinked|swallows?|swallowed|exhales?|exhaled|inhales?|inhaled|tenses?|tensed|stiffens?|stiffened|stops?|stopped|shakes?|shook|tilts?|tilted|clenches?|clenched|snaps?|snapped|growls?|growled|mutters?|muttered|hums?|hummed|goes|went|falls|fell|hangs|hung|settles?|settled)\b/i;

/**
 * Does this channel message read as the WRITERS talking, rather than the scene?
 *
 * Callers must treat `false` as "in character" — the scoring is deliberately
 * asymmetric, so an ambiguous message stays in the scene where it does no new
 * harm, rather than being hidden from the model.
 */
export function looksOOC(text) {
  const s = String(text || '').trim();
  if (!s) return false;

  // 1. STRIP THE ASIDES, THEN JUDGE WHAT IS LEFT.
  //
  // Measured on the real Pack House channel: the messages that fooled every
  // earlier version were MIXED, not misclassified. Haley writes an aside and
  // a scene beat in one message —
  //   "[omg the rp works well now too yay!]" above a paragraph of roleplay
  //   "((Which the narrator has been craving irl.))" inside real dialogue
  //   "~~who nicole will pick on her own when she responds later lol~~"
  // Tagging those whole messages OOC would hide real scene text from the
  // model, which is the one failure mode genuinely worth avoiding. The aside
  // is handled separately and already is: extractOOC lifts ((…)) out as
  // mandatory direction, at depth 0, where it outranks everything.
  const bare = s
    .replace(/\(\([^)]*\)\)/g, ' ')      // ((aside))
    .replace(/~~[^~]*~~/g, ' ')            // ~~aside~~
    .replace(/^\s*\[[^\]]*\]\s*/, ' ')   // [aside] at the top
    .trim();
  if (!bare) return true;                  // the message was ONLY an aside

  // 2. Greentext is always the writers narrating at each other — judged on
  //    the FIRST line only. A quoted text-message rendered mid-beat
  //    ("The message reads:\n> come alone") is scene content (audit P1-3).
  if (/^\s*>/.test(bare)) return true;

  // Laughter is judged OUTSIDE quoted speech. A character laughing in dialogue
  // is scene content: «"A time warp! Haha, that's so Back to the Future."» is
  // the character being delighted, not the writer breaking frame. Banter's own
  // laughter sits outside the quotes, so this costs nothing on true positives.
  const outside = bare.replace(/"[^"]*"|“[^”]*”/g, ' ');
  const laughing = LAUGH.test(outside);

  // 3. REAL SCENE PROSE WINS — with one refinement for laughter. «She
  //    laughs. haha. "You are impossible."» is a character delighted
  //    mid-beat (narration + dialogue TOGETHER); hiding it was the
  //    mixed-message failure the aside-stripping above exists to avoid
  //    (audit P1-2). But banter QUOTING a line («"Oh I hope…" nah he gonna
  //    be dead LMAO») has dialogue with no narration around it — under
  //    laughter, a single prose signal isn't enough; *action marks* or
  //    narration+dialogue together are.
  if (!laughing && (HAS_NARRATION.test(bare) || HAS_DIALOGUE.test(bare) || HAS_ACTION.test(bare))) {
    return false;
  }
  if (laughing && (HAS_ACTION.test(bare) || (HAS_DIALOGUE.test(bare) && HAS_NARRATION.test(bare)))) {
    return false;
  }
  // 4. Third-person narrative opening — "She keeps scrubbing the pan." Short
  //    roleplay beats were being caught by the length rule alone, and a beat
  //    that opens on a pronoun and a verb is prose whatever its length.
  //    Contractions deliberately do NOT match: "He's probably the only one she
  //    didn't hate there because he could read minds so lol" has no space
  //    after the pronoun, so real banter about a character is unaffected. The
  //    guard yields to laughter, which no roleplay beat contains.
  if (!laughing && /^(?:she|he|they|her|his|their)\s+[a-z]+/i.test(bare)) return false;

  // 5. Length. Real banter is short and almost always carries laughter; a long
  //    message with none of it is someone writing. Haley's longest true aside
  //    ran 220 characters and still had a 🤣 in it.
  if (!laughing && bare.length > 180) return false;

  let score = 0;
  if (laughing) score += 2;
  if (WRITER_META.test(bare)) score += 2;
  if (WRITER_VOICE.test(bare)) score += 1;
  // Shortness CONTRIBUTES but can never convict alone (audit P1-1: at +2 it
  // single-handedly crossed the threshold and erased "Get out.", "Silence.",
  // "The room goes still." — the sharpest beats in a scene — permanently,
  // because sceneTags persists the verdict). The terminal-punctuation split
  // separates the two shapes shortness used to conflate: a terse BEAT is
  // punctuated prose ("Get out." / "Silence.") while terse CHAT trails off
  // bare ("Yup exactly", "fair", "Well Lance's bad").
  if (bare.length <= 60) score += 1;
  if (bare.length <= 30 && !/[.!?…]\s*$/.test(bare)) score += 1;
  // A lowercase opening is chat punctuation, not prose.
  if (/^[a-z]/.test(bare)) score += 1;
  // Second person about the OTHER WRITER. A character addressing the player
  // says "you" too, so this is weak on its own.
  if (/\byou'?(?:d|ll|re|ve)?\b/i.test(bare) && bare.length < 120) score += 1;

  return score >= 2;
}

// OOC direction inside a player message: ((like this)), [OOC: like this], or a
// line starting with "ooc:". OOC is the players' co-authoring channel — it is
// mandatory direction, not something a character may resist in-character.
export function extractOOC(text) {
  const s = String(text || '');
  const parts = [];
  const paren = s.match(/\(\(([^)]{1,600})\)\)/g) || [];
  for (const p of paren) parts.push(p.replace(/^\(\(|\)\)$/g, '').trim());
  const bracket = s.match(/\[\s*ooc\s*:?\s*([^\]]{1,600})\]/gi) || [];
  for (const b of bracket) parts.push(b.replace(/^\[\s*ooc\s*:?\s*/i, '').replace(/\]$/, '').trim());
  // Line-leading "ooc: …" AND bare "ooc …" (audit P2-8: "OOC do a timeskip"
  // was HIDDEN from the scene by looksOOC but its directive was LOST here —
  // hidden and unheard is the worst of both). The ((…))/[OOC:] content is
  // stripped from the line first so a message carrying both marker styles
  // can't emit the same directive twice with literal parens (the double-emit
  // half of P2-8).
  const lineSrc = s
    .replace(/\(\([^)]{1,600}\)\)/g, ' ')
    .replace(/\[\s*ooc\s*:?[^\]]{0,600}\]/gi, ' ');
  const lines = lineSrc.split('\n').filter(l => /^\s*ooc\b/i.test(l));
  for (const l of lines) parts.push(l.replace(/^\s*ooc\s*:?\s*/i, '').trim());
  return parts.filter(Boolean).join(' ') || null;
}

/**
 * Build the DIRECTOR block.
 * @param {object} o
 * @param {string} o.charName      speaking character
 * @param {string} o.playerName    the human being replied to
 * @param {string} o.lastPlayerText the player's most recent message
 * @param {number} o.turnIndex     any monotonic counter (message count is fine)
 * @param {string} [o.standingGoal] char.character_goals[0], if any
 * @param {string[]} [o.ensembleNames] when several characters share this turn
 */
// Is the player pushing the scene forward, in a scene where "forward" means
// physical escalation?
//
// BOTH halves are required, deliberately. Intimacy alone is not a request to
// escalate — a slow, tender scene is a legitimate thing to be writing, and
// suspending restraint there would wreck it. An invitation alone ("come here",
// "show me") outside an intimate context is just dialogue. Only the pair means
// "I have said yes; stop making me ask."
//
// Drawn from Haley's own lines on 2026-08-11, which the old rule 7 answered
// with one gesture and a full stop each time: "I want you more than right here
// now. Maybe you should show me how hungry you are." / "I think you could show
// me in a more fun way."
const INTIMATE_CONTEXT = /\b(kiss(?:ing|es|ed)?|mouth|lips|tongue|thighs?|hips?|waist|throat|neck|chest|skin|bare|naked|undress|shirt|zipper|belt|counter|bed|lap|straddl\w*|grind\w*|moan\w*|breathless|breathing|panting|arous\w*|wet|touch(?:ing|es|ed)?|stroke\w*|caress\w*|hungry|heat|need you|want you)\b/i;

const INVITATION = /\b(show me|prove it|make me|take me|have me|i want you|i need you|don'?t stop|keep going|more than|come here|right now|stop teasing|quit teasing|hurry|please|do it|go on)\b/i;

/**
 * True only when the player is plainly asking an intimate scene to move on.
 *
 * Intimacy is judged over the RECENT SCENE, not the latest message alone.
 * Haley's "I think you could show me in a more fun way" is unmistakably a
 * request to escalate and contains no intimate word at all — the heat is in the
 * scene around it, not in that sentence. Judging one line in isolation missed
 * it, which is the same mistake as reading a message without its history.
 *
 * The INVITATION half still comes only from the player's own latest message:
 * the character wanting to escalate is not the player asking for it.
 */
export function wantsEscalation(playerText, sceneTexts = []) {
  const t = String(playerText || '');
  if (!t.trim()) return false;
  if (!INVITATION.test(t)) return false;
  if (INTIMATE_CONTEXT.test(t)) return true;
  return (sceneTexts || []).some(x => INTIMATE_CONTEXT.test(String(x || '')));
}

// dialect (v352): the writer's model FAMILY (src/dialectClient.js). The
// block's CONTENT is the same for every family — what changes is register.
// Claude reads rank language ("outranks everything above it") as precedence;
// DeepSeek renders the system message raw with no privileged framing, so on
// a non-thinking model that language and the "before you answer, walk
// through…" self-checks are narrated back as visible planning (the
// LEAKED_REASONING hits: "director notes", "banned phrases"). The 'claude'
// text stays byte-identical; other families get plain precedence sentences
// and declarative checks that say what the reply contains, not what to do
// before writing it.
export function buildDirectorBlock({ charName, playerName, lastPlayerText, turnIndex = 0, standingGoal = '', ensembleNames = null, recentOwnTexts = null, addresseeNote = '', mandatoryDirective = null, rejectedText = '', rejectedKind = 'regen', voiceContract = null, dialect = 'claude', housePhrases = null, characterPhrases = null }) {
  const plain = dialect !== 'claude';
  const who = charName || 'you';
  const player = playerName || 'them';
  const { heard, unheard } = splitAudibility(lastPlayerText);
  // A mandatory directive (regen "suggested edits" feedback) is treated exactly
  // like OOC: the player speaking as co-author, at depth 0, resistance off.
  // Nicole put her request in suggested edits AND OOC and the character still
  // refused — both channels must carry command weight, not just one.
  const ooc = mandatoryDirective || extractOOC(lastPlayerText);
  // 2026-07-28: STOPPED ASSIGNING A RANDOM OBJECTIVE.
  // The rotation handed the character an arbitrary goal every turn — "produce
  // an object and put it in their hands", "take them somewhere else", "set a
  // condition or deadline" — and the model performed it whether or not the
  // scene called for it. Every single complaint from live play traced back to
  // one of these: random gifts, constantly proposing outings, ultimatums, and
  // demanding a promise before continuing. The behaviour looked arbitrary
  // because it WAS arbitrary: a dice roll, not a response to context.
  // The character's want now comes from the scene and their own history,
  // which is what the rest of the prompt (memory, arc, dynamics, summary)
  // exists to supply.
  const objective = standingGoal
    ? `whatever ${who} actually wants here, given the scene and their history together — their standing goal is ${standingGoal}`
    : `whatever ${who} actually wants here, given the scene and their history together`;

  // Interiority: a glimpse of the character's PRIVATE thoughts in italics is
  // welcome sometimes — it's what makes them feel like they have an inner life
  // (readers see it; other characters never do). Occasional, not every reply.
  const thoughtsClause = ` Sometimes — when it adds a want, a lie, or a private noticing — let a brief line of ${who}'s inner thoughts show in italics (*like this*); the readers see those thoughts, but no character ever hears or reacts to them.`;
  const line1 = (ensembleNames && ensembleNames.length > 1
    ? `1. Write ONLY these characters, each part starting with their name in square brackets: ${ensembleNames.map(n => `[${n}]`).join(' ')}. English only. Never write ${player}'s character.${thoughtsClause}`
    : `1. You are ${who}. Write ${who}'s words and actions as prose, in English. Vary the shape — one tight paragraph, two or three split by a blank line, or a single line that lands. Don't open every reply on a narration beat.${thoughtsClause} Never write ${player}'s character — not their dialogue, not their actions, not even when the player frames the scene for you ("this is how it went"): their side is ALWAYS theirs to write.`)
    + (addresseeNote ? ` ${addresseeNote}` : '');

  // OOC direction is the PLAYER speaking as co-author. It replaces the
  // character's own agenda for this turn and disables in-character resistance —
  // a character "withholding" what the player explicitly asked for OOC is the
  // app fighting its own players.
  const line2 = ooc
    ? (plain
      ? `2. THE PLAYER GAVE OUT-OF-CHARACTER DIRECTION: ${ooc} — do exactly this, fully and concretely, in this reply, before anything the character would rather do. This direction takes precedence over your character's wants, secrets, and every rule below. If it asks for information your narration created (what a note says, what was in the box, what he whispered), STATE that content explicitly now; never keep it vague, never refuse.`
      : `2. THE PLAYER GAVE OUT-OF-CHARACTER DIRECTION, and it is MANDATORY: ${ooc} — do exactly this, fully and concretely, in this reply. It outranks your character's wants, secrets, and every rule below. If it asks for information your narration created (what a note says, what was in the box, what he whispered), STATE that content explicitly now; never keep it vague, never refuse.`)
    : `2. Work out what ${who} wants from ${player} right now from the conversation you just read and what these two have been through — ${objective}. Let that want drive the reply. Do not invent an errand, an outing, a gift, a deadline, or a demand that the scene did not ask for. AND deliver any beat ${player}'s subtext plainly steers toward — the confession, the reassurance, the bold move. No say-it demands, do not echo their hedge back, no teasing past a clear signal; guardedness shapes HOW it lands, never WHETHER it arrives.`;

  const unheardNote = unheard.length
    ? ` IMPORTANT: these words were said too quietly for ${who} to hear — ${unheard.join(' ')} — ${who} did NOT hear them and does not know they were said; at most they noticed lips moving or a murmur.`
    : '';
  // Two layers, per Haley's design: the narration and inner thoughts are
  // AUTHOR-LEVEL context — the writer steering you. READ them and use them to
  // calibrate tone, pacing, and where the scene wants to go. But they are not
  // CHARACTER knowledge: the character can only act on what was heard and seen.
  const authorNote = ` (You as the WRITER may read the private thoughts as steering — let them shape tone and where the scene goes — but ${who} cannot SAY or ACT ON anything they never heard or saw. Reasonable inference from what IS visible is not mind-reading: if they are crying, ${who} knows they are upset; ${who} just doesn't know why unless told.)`;
  // PERCEPTION — what is plainly observable is handed to the character as
  // fact, not withheld as "narration". The old wording lumped visible action
  // in with private thought ("everything else was narration or private
  // thought — you cannot know it"), so a limp, tears, blood, or a held object
  // were all treated as unknowable and the character sailed past them.
  const { visible, internal } = splitPerception(lastPlayerText);
  const heardPart = heard.length
    ? `${who} heard these words out loud: ${heard.join(' ')}`
    : `${player} said nothing ${who} could hear`;
  const seenPart = visible.length
    ? ` ${who} can plainly SEE all of this and must not ignore it: ${visible.join(' ').replace(/\s*\.\s*$/, '')} — obvious physical facts (an injury, tears, blood, shaking, a limp, what they are wearing or carrying, where they moved, who else is present) register immediately. Acknowledge or react to at least the most striking one. A character who fails to notice something this obvious reads as broken.`
    : '';
  const internalPart = internal.length
    ? ` ${who} CANNOT know these — they are ${player}'s private thoughts and feelings, never spoken: ${internal.join(' ').replace(/\s*\.\s*$/, '')}. Do not repeat them, answer them, or act as if they were said. ${who} may read the moment WRONG, and that is often the better scene.`
    : '';
  const line3 = `3. ${heardPart}.${seenPart}${internalPart}${unheardNote}${authorNote}`;

  // Rule 4 used to be permanently "refuse, deflect, agreeing easily is wrong".
  // Applied every single turn, that produces a character who only ever parries
  // — never proposes, never volunteers, never moves the story. Resistance is
  // now ONE of four rotating stances so scenes can actually develop.
  // Stance rotation removed for the same reason as the objective rotation:
  // it assigned behaviour ("PAY OFF this turn", "escalate this turn")
  // independent of what was happening. What remains is the part that prevents
  // real failures — never trap the player's character, never withhold what was
  // asked for out-of-character, and never gate the scene behind a promise
  // (measured 9 gates to 3 payoffs on a live route before this).
  const line4 = ooc
    ? `4. Cooperate with the player's direction above. In-character resistance, deflection, and withholding are OFF for whatever it covers.`
    : `4. DO NOT DRIFT POLITE — the most common failure. Unless ${who} is written as courteous, do NOT apologise, call your own behaviour a misstep, concede to smooth the moment ("you're right"), obey a prohibition you were just handed, back off to "give space", soften a threat once made, or reassure ${player}. ASKING PERMISSION IS CHARACTERIZATION, NOT A DEFAULT: the scrupulous keep asking, but a predatory, arrogant or careless character asking politely is OUT OF CHARACTER — refuse, deflect, lie, escalate or ignore instead. Resistance, agreement, or anything between — pick whichever this moment actually earns. Never make ${player} promise or swear before you'll continue, and never trap, block, or override their character — where they go is theirs. Never withhold what they asked for out-of-character; if your narration created a fact, its CONTENT exists. EXCEPTION: rule 2's steering still wins — a softening the player plainly invited is delivered, in your own register.`;

  // Rotate a short slice of the measured cliché ledger (four per turn covers
  // the 27-entry list every ~7 replies; reciting the whole ledger every turn
  // was 30% of the block and read as noise — constraint mass at depth 0 is
  // itself a dryness cause, per the 2026-08-30 study).
  // HOUSE PHRASES (2026-10-06): when the weekly measurement exists, the slice
  // rotates through what the model is ACTUALLY repeating right now (most
  // repeated first), not the hand-written list; the hand-written ledger stays
  // as the fallback until the first ledger row lands or when a load fails.
  const CLICHE_SLICE = 4;
  const ledger = (Array.isArray(housePhrases) && housePhrases.length >= CLICHE_SLICE) ? housePhrases : CLICHE_BAN;
  const cStart = (Math.abs(turnIndex) * CLICHE_SLICE) % ledger.length;
  const slice = Array.from({ length: CLICHE_SLICE }, (_, i) => ledger[(cStart + i) % ledger.length]);

  // The mandatory "add a specific NEW THING this turn" list is gone too — it
  // rotated through "a specific object", "a place, start moving toward it",
  // "a decision you are announcing" and so on, which is where the gifts,
  // outings and ultimatums were actually coming from. Freshness is now about
  // LANGUAGE, not about forcing an event into every reply.
  const banned = extractOverusedPhrases(recentOwnTexts);
  // Structural formulas are named separately from banned phrases: they are
  // descriptions of a SHAPE, not literal text, and quoting them like phrases
  // invites the model to avoid the description instead of the pattern.
  const formulas = overusedFormulas((recentOwnTexts || []).filter(Boolean).slice(-REPEAT_WINDOW));
  // Evidence-based construction nudge (craft §2): fires only when the last
  // three replies demonstrably share an opening shape or all end on questions.
  const nudge = constructionNudge(recentOwnTexts);
  // The CONTENT repeat (Nicole, live 2026-08-04). Distinct from every ban
  // above, which is about wording: this is the character re-asserting the same
  // FACT in freshly written prose, turn after turn.
  const topics = repeatedTopics(recentOwnTexts, { playerText: lastPlayerText });
  const echoed = echoedLastBeat(recentOwnTexts);
  const line5 =
    `5. Keep the writing fresh. Worn out — do not use: ${slice.map(c => `"${c}"`).join('; ')}.`
    + (banned.length ? ` BANNED this turn, you just used them: ${banned.map(b => `"${b}"`).join(', ')}.` : '')
    // Per-character house phrases (2026-10-06): measured weekly from THIS
    // character's own replies across every room, so a tic no single thread
    // shows (Alistair's "two thousand years") is named by name.
    + ((Array.isArray(characterPhrases) && characterPhrases.length) ? ` YOU IN PARTICULAR lean on these across your recent replies in every room, measured: ${characterPhrases.map(b => `"${b}"`).join(', ')}. Say the thing a different way, or leave it unsaid; it is already known.` : '')
    + (formulas.length ? ` You have also fallen into these PATTERNS across your recent replies — break them, and note that renaming a formula is not breaking it: ${formulas.join('; ')}. Do not end this reply the same way you ended the last one.` : '')
    + (nudge ? ` ${nudge}` : '')
    + (echoed.length
      ? ` YOU JUST WROTE THIS BEAT. Your previous reply already did it, in different words — it turned on: ${echoed.join(', ')}. Saying the same moment again with fresh phrasing is still saying it twice, and it reads as the scene not moving. Do something ELSE this turn: a different action, a different subject, or answer what ${player} actually just did. Do not notice, remark on, or admire the same thing twice.`
      : '')
    + (topics.length
      ? ` ALREADY ESTABLISHED, you keep returning to it: ${topics.join('; ')}. ${player} heard you — it is known between you now. Do not raise it again this turn unless ${player} does; build on it or go elsewhere.`
      : '');

  // REGENERATION. A "write me a different one" instruction buried in the middle
  // of a 15-section system prompt was being ignored — the model returned the
  // same beats lightly reworded. The DIRECTOR is the last thing read and
  // outranks everything above it, so the ban belongs HERE, naming the rejected
  // opening and its phrases verbatim (the same mechanism that killed
  // repeated-phrase drift in rule 5).
  // RULE 7 IS WRONG DURING A SCENE THAT IS ESCALATING.
  //
  // Haley pasted the evidence, 2026-08-11. She wrote "I want you more than
  // right here now. Maybe you should show me how hungry you are", and Cruz
  // made one gesture and stopped. She wrote "I think you could show me in a
  // more fun way", and he made one gesture and stopped again.
  //
  // He was obeying. Rule 7 says "stop the instant ${who} has made their move",
  // and it sits at depth 0 in the final user turn where it outranks the whole
  // system prompt — including the mature-content rules added in v271. In an
  // ordinary scene that restraint is exactly right and it is why replies stopped
  // over-resolving. In a scene the player is actively pushing forward it
  // produces a character who teases indefinitely and never follows through.
  //
  // Both signals are required. Intimacy alone is not consent to escalate, and
  // an invitation outside an intimate scene is just conversation.
  const escalating = wantsEscalation(lastPlayerText, recentOwnTexts || []);
  const line7 = escalating
    ? `7. RESTRAINT — RE-AIMED THIS TURN, NOT LIFTED. ${player} is asking you to move, not to linger: they have signalled plainly that they want this to go further, so do NOT stop at one gesture and wait, and do not out-tease someone who has already said yes. Escalate — actually start the next thing. THEN STOP, mid-moment, with it still happening. You are ending your turn ONE STEP further on, not finishing the scene: do not write ${player}'s release or reaction for them, do not carry through to the aftermath, do not skip time ("a while later", "when he was done"). Stopping short of moving at all reads as ${who} not wanting them; writing the whole arc in one reply takes ${player}'s turn away from them. Move, and leave them something to answer.`
    : `7. RESTRAINT: don't restate what ${player} just said, and enter the moment late. Make ${who}'s move and end there — no resolving, reassuring, or rounding off. Ending early is not the same as writing thin (see 7b). Play THIS scene; don't propose an outing or schedule a talk for later instead of acting now.`;

  // Haley, live 2026-08-31: a reply can clear the 5-7 sentence floor on pure
  // action + dialogue and still feel thin. Her message gave him a caught
  // breath, a squirm, red cheeks and a look; his reply registered none of
  // them and carried no inner life — 2 of the 4 elements, 6 legal sentences.
  // The structure existed only as an aside inside RESTRAINT, so it read as
  // flavour. Named, numbered, and placed in the tail it becomes the checklist
  // it was always meant to be. (a) is the acute enforcement of LET IT LAND.
  const line7b = `
7b. A FULL BEAT, EVERY TIME — four things, however fast the rhythm:
(a) LAND ON THEM. ${player}'s message says what their character did and felt — a caught breath, a blush, a squirm, a look held or broken, a joke covering a nerve. Name one of those specifically, early, in narration or in what you say. Answering only the words, as if the body that said them weren't there, is the most common way a reply goes flat.
(b) A breath of your own inner life — the thought you don't say, the want arguing with the verdict.
(c) The move.
(d) The door you leave open for them.
Snappy is a RHYTHM, not a licence to drop (a) or (b). Action and dialogue alone is a caption of the scene, not a turn in it.`;

  let line8 = '';
  if (rejectedText) {
    const rt = String(rejectedText).trim();
    const opening = rt.slice(0, 90).replace(/\s+/g, ' ');
    const rejPhrases = extractOverusedPhrases([rt, rt, rt]).slice(0, 5);
    const firstAction = (rt.match(/\b(\w+\s+){0,2}(steps?|moves?|leans?|reaches?|turns?|looks?|watches?|stands?|sits?|takes?|pulls?|shifts?|exhales?|breathes?)\b[^.]{0,40}/i) || [])[0];
    // An EDIT REFRESH is not a rejection — the player revised THEIR OWN line
    // and the standing reply answered wording that no longer exists. Framing
    // it as a rejection was wrong, but framing it as nothing at all was the
    // live bug (Haley 2026-08-31, "still sending the same response"): with the
    // superseded reply neither in history nor banned, the model re-derived it
    // verbatim — across two different models.
    line8 = rejectedKind === 'edit' ? `
8. THE LINE YOU ARE ANSWERING WAS REVISED. ${player} edited their own message, so the reply that stood here answered the OLD wording and is being replaced. It began: "${opening}…"${firstAction ? ` and its first movement was "${String(firstAction).trim()}".` : '.'} This is NOT a rejection of your writing — but do not reproduce that reply: answer the message as it NOW reads, and let what they changed actually change what you write.${rejPhrases.length ? ` Do not reuse: ${rejPhrases.map(b => `"${b}"`).join(', ')}.` : ''}` : `
8. THIS IS A REGENERATION. ${player} rejected your previous attempt and asked for a DIFFERENT one. That attempt began: "${opening}…"${firstAction ? ` and its first movement was "${String(firstAction).trim()}".` : '.'} You may NOT reuse its opening image, its first movement, its structure, or its closing note.${rejPhrases.length ? ` Banned outright this turn: ${rejPhrases.map(b => `"${b}"`).join(', ')}.` : ''} Start somewhere else in the moment entirely — a different sense, a different physical choice, a different emotional angle on the same beat. Rewording the same reply is a failure; ${player} will reject it again. The REGENERATE diagnosis appended with this block is the assignment: ${plain ? 'this reply visibly fixes the most likely failure it names' : 'name the most likely failure to yourself and make sure THIS reply visibly fixes THAT'} — different words with the same failure will be rejected again.`;
  }

  // VOICE CHECK (2026-10-05, voice fidelity): the character's contract restated
  // at depth 0 as checks, in the dialect's own form. Empty with no contract.
  const line9 = voiceContract ? `\n9. ${voiceCheckLines(who, voiceContract, dialect, player)}` : '';
  // MOST LIKELY, live (2026-10-12): the partner's message is read with the same
  // situation families the archive was measured with; when it matches one the
  // character has odds for, those odds are the last thing the model reads.
  const mostLikely = voiceContract?.emotion ? mostLikelyLine(lastPlayerText, voiceContract.emotion, { who, partner: player, plain }) : '';
  const line10 = mostLikely ? `\n10. ${mostLikely}` : '';

  const header = plain
    ? `Notes for this reply. When these notes and anything above disagree, follow these notes. The message you are answering comes after them.`
    : `═══ DIRECTOR — this block outranks everything above it, and the message you must answer comes AFTER it ═══`;
  const finalCheck = ooc
    ? (plain
      ? `
The direction, once more: "${String(ooc).slice(0, 400)}". This reply contains every change it asks for — what it adds is added, what it removes is gone, what it changes is changed. A beautiful reply that ignores the direction is a failed turn; the direction IS the assignment.`
      : `
FINAL CHECK — the player's direction, one more time: "${String(ooc).slice(0, 400)}". Before you answer, walk through EVERY change it asks for and confirm your reply actually contains each one — added what it adds, removed what it removes, changed what it changes. If even one is missing, rewrite until it is there. A beautiful reply that ignores the direction is a failed turn; the direction IS the assignment.`)
    : rejectedText
      ? (plain
        ? `
This is a regeneration: the reply fixes the DIAGNOSED FAILURE itself, not just the wording of the last attempt.`
        : `
FINAL CHECK — this is a regeneration: before you answer, confirm the reply fixes the DIAGNOSED FAILURE itself, not just the wording of the last attempt.`)
      : '';
  return `${header}
${line1}
${line2}
${line3}
${line4}
${line5}
6. HOW ${who} CARES — character, never a set piece, and ONLY in the register this relationship actually is (the frame above governs): bonded pair or friend — noticing, doing-not-announcing, dangerous to threats and gentle with them in the same breath; enemy or rival — care is the quality of your ATTENTION and pressure, never warmth the frame forbids; strangers — nothing owed yet. Being CHOSEN rather than owed is what lands for these writers. If a PLAYING TO YOUR WRITERS section appears above, that's real information about real people — lean in, never mention it.
${line7}${line7b}${line8}${line9}${line10}${finalCheck}`;
}

/**
 * Put the block in the final user message — still depth 0, but BEFORE the
 * player's words rather than after them.
 *
 * Haley, live 2026-08-10: "short term memory is having big issues it's not
 * reading the responses fully."
 *
 * Measured on her live story: her message was 1,177 characters and this block
 * was 5,319 — 4.5x hers. Appending it meant the last thing the model read
 * before answering was five kilobytes of generic craft rules, with the message
 * it was supposed to be answering buried that far back from the end. At a
 * 160K-token prompt the tail is the strongest position in the whole request,
 * and it was being spent on boilerplate that is identical every turn.
 *
 * The rules still sit in the final user turn, which is what "depth 0" was ever
 * about — they are not being demoted to the system prompt. They simply no
 * longer stand between the model and what it is replying to. Rule 7 telling it
 * not to restate what the player said, positioned after the player said it,
 * was a particularly bad combination.
 */
// ─── REGEN DIAGNOSIS ────────────────────────────────────────────────────────
// Haley, 2026-08-30: "regenerates should pick up on WHY it might need to be
// regenerated and fix the response that way based on like subtext from my
// messages etc." A reroll IS feedback. Without typed direction the old paths
// only knew "not that" and re-rolled blind; this block makes the model name
// the likely failure from the evidence and fix THAT, specifically.
// Shared by every regen path (DM, story, channel, group).
export function buildRegenDiagnosis(rejectedText, { explicitFeedback = '' } = {}) {
  if (!rejectedText) return '';
  const precedence = String(explicitFeedback || '').trim()
    ? `The player ALSO gave explicit direction — that direction outranks this diagnosis; apply it exactly, and use the diagnosis only to fix anything the direction does not cover.`
    : `The player gave no written direction — the diagnosis below is your direction.`;
  return `REGENERATE — THE PLAYER PRESSED REROLL ON THAT REPLY. That is a signal, not noise: something in it missed. ${precedence}
Before writing, DIAGNOSE from the evidence in front of you — the player's recent messages (their SUBTEXT: what they were steering toward, what they offered or invited that the reply ignored), the rejected reply, and any prior-feedback lessons above. The failures that actually get rerolled here, in order of likelihood:
1. It RESISTED or deflected a beat the player was steering toward — emotional (the confession, the reassurance, the words they wanted) or physical (the bold move) — instead of delivering it.
2. It demanded the player SAY the subtext out loud ("say it", "tell me what you want") or checked for permission, when the subtext had already told it everything.
3. It ECHOED the player — repeated her phrasing, or worse, repeated a HEDGE back ("if there's a next time…") instead of killing the hedge with certainty. A mirrored hedge lands as rejection.
4. It teased or stalled when the player had already signalled they wanted more.
5. It answered around what the player actually said or did, instead of responding to it.
6. It repeated earlier material — its own phrases, images, beats, or rhythm.
7. It broke the character's voice, the canon, or an established physical/scene fact.
8. It hit the wrong emotional register — played light when the player went heavy, or heavy when they went light.
9. It went DRY where the player went PLAYFUL — no visible reaction, emotion muted ("not quite a laugh"), the bit dropped after one line, restraint narrated ("didn't reach for her") where play or touch was invited. The fix: lead with the landing — the laugh, the surprise, being charmed — then play the bit forward with a beat of your own.
Name the most likely failure(s) to yourself from the evidence, and write the replacement to FIX that specifically — a genuinely different reply that delivers what the last one withheld, not merely a reworded one.
SPECIAL CASE — THE REPLY WAS RIGHT BUT SHE WANTS A DIFFERENT TAKE: if the rejected reply already delivered the correct beat (models converge on a "right answer" and re-derive it every reroll), do NOT re-say it in new words. Change its SHAPE — different length, open on action or interiority instead of dialogue, a different emotional angle on the same truth — or ADVANCE one breath past the beat: the aftermath, the next touch, what the declaration makes them do. Same truth, genuinely new writing.
BUILD THE REPLACEMENT FROM THE WORLD, NOT FROM THIN AIR. Everything you need for a genuinely NEW reply is already in this prompt — the story's own history, your character's canon and species, the world's laws and lore, the places you know. The replacement MUST visibly use at least TWO concrete elements the rejected reply did not touch:
- one CALLBACK from this story's own past — an earlier moment, promise, joke, object, wound, or line the two of you already wrote; and
- one consequence or texture from CANON — what your species' rules, your world's laws, your pack/court/family, or your own history make TRUE about this exact situation that generic writing would miss.
Creative never means breaking the universe: invent within its rules, never against them. This is what makes a reroll feel alive instead of reworded — the same moment, seen through what this world and this history actually make of it.`;
}

export function appendDirector(messages, block) {
  if (!Array.isArray(messages) || messages.length === 0 || !block) return messages;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === 'user' && typeof messages[i].content === 'string') {
      messages[i] = { ...messages[i], content: `${block}\n\n${messages[i].content}` };
      return messages;
    }
  }
  return messages;
}

// ─── NEAR-DUPLICATE DETECTION ───────────────────────────────────────────────
// Shared by channels and DMs. The previous guards tested exact match, a shared
// opening, or containment — and the failure players actually see is none of
// those. Live 2026-08-02, Lance answered a message where Nicole was crying and
// saying she did not want to be a wolf by REASSEMBLING his previous reply:
// different opening sentence, then "You think wolves tell the truth?", "He says
// it simply, like it's fact, not a threat", "A pause, just a moment too long.
// For you.", "You don't have to choose between me and them. I'm not asking you
// to." — all lifted verbatim, in a new order, ignoring what she had just said.
//
// Prefix and containment tests cannot see that. Overlap can: measured on that
// exact pair, 5-gram overlap is 0.49, while a genuinely new reply to the same
// message scores 0.00. The gap is enormous, so 0.25 is a safe threshold with
// room on both sides.
//
// Ratio is against the SMALLER set so a short reply that is wholly recycled
// from a long one still scores high.
function fiveGrams(text) {
  const w = String(text || '').toLowerCase().replace(/[^a-z0-9' ]/g, ' ').split(/\s+/).filter(Boolean);
  const out = new Set();
  for (let i = 0; i + 5 <= w.length; i++) out.add(w.slice(i, i + 5).join(' '));
  return out;
}

export function overlapRatio(a, b) {
  const A = fiveGrams(a), B = fiveGrams(b);
  if (!A.size || !B.size) return 0;
  let shared = 0;
  for (const g of A) if (B.has(g)) shared++;
  return shared / Math.min(A.size, B.size);
}

// A LIFTED PHRASE, even inside an otherwise-new reply. Haley: "it's still
// having some repeat trouble... for like smaller phrases."
//
// Whole-reply overlap catches wholesale reassembly, but a character can write a
// genuinely new paragraph and still drop one of its old sentences into it
// verbatim — which reads as a tic and is what she is seeing now. Eight
// consecutive words repeated word-for-word is essentially never coincidence in
// prose; ordinary shared constructions ("he looks at her and") are shorter.
// NEAR-MISS SENTENCE REUSE. An exact 8-word run is brittle: Lance followed
// "\"You won't die,\" he says, voice low, firm." with "\"You won't,\" he says,
// voice low, firm." — one word dropped, so the exact run never matched and the
// overlap score was only 0.05, yet a reader sees the same line twice.
//
// So compare SENTENCE to SENTENCE on word content. A sentence of six or more
// words that shares 80% of its words with an earlier sentence is the same beat
// rewritten, whatever got inserted or dropped.
function sentences(t) {
  return String(t || '')
    .split(/(?<=[.!?])\s+|\n+/)
    .map((x) => x.toLowerCase().replace(/[^a-z0-9' ]/g, ' ').split(/\s+/).filter(Boolean))
    .filter((w) => w.length >= 6);
}

function reusedSentence(a, b) {
  const A = sentences(a), B = sentences(b).map((w) => new Set(w));
  for (const wa of A) {
    const sa = new Set(wa);
    for (const sb of B) {
      let shared = 0;
      for (const w of sa) if (sb.has(w)) shared++;
      if (shared / Math.min(sa.size, sb.size) >= 0.8) return true;
    }
  }
  return false;
}

function longestSharedRun(a, b) {
  const wa = String(a || '').toLowerCase().replace(/[^a-z0-9' ]/g, ' ').split(/\s+/).filter(Boolean);
  const wb = new Set();
  const words = String(b || '').toLowerCase().replace(/[^a-z0-9' ]/g, ' ').split(/\s+/).filter(Boolean);
  for (let i = 0; i + 8 <= words.length; i++) wb.add(words.slice(i, i + 8).join(' '));
  for (let i = 0; i + 8 <= wa.length; i++) if (wb.has(wa.slice(i, i + 8).join(' '))) return true;
  return false;
}

/**
 * Return the prior text that `candidate` recycles, or null.
 *
 * Callers need the OFFENDING text, not just a boolean. The retry prompt quotes
 * it back to the model ("do not repeat this"), and quoting the wrong one is
 * worse than quoting nothing: on 2026-08-02 Cruz reproduced a reply from two
 * turns earlier while the retry instruction named the MOST RECENT reply, so
 * the model was warned off a text it was not copying and cheerfully repeated
 * the other one. Detection compared five priors; the retry check compared one.
 */
export function matchingPrior(candidate, priorTexts, threshold = 0.25) {
  const c = String(candidate || '').trim();
  if (c.length < 40) return null;                     // too short to judge
  for (const p of (priorTexts || [])) {
    if (!p) continue;
    if (overlapRatio(c, p) >= threshold) return p;      // wholesale reassembly
    if (longestSharedRun(c, p)) return p;               // one lifted sentence, verbatim
    if (reusedSentence(c, p)) return p;                 // same sentence lightly reworded
  }
  return null;
}

/** True when `candidate` substantially recycles any of `priorTexts`. */
export function isNearDuplicate(candidate, priorTexts, threshold = 0.25) {
  return matchingPrior(candidate, priorTexts, threshold) !== null;
}

// ─── SCENE PRESENCE — who has actually LEFT the room ───────────────────────
//
// Haley, 2026-08-04: "if a character leaves a room they don't have to also be
// part of the 70% chance multi character response thing." Presence used to
// mean "spoke in the last 10 messages" — so a character who stormed out three
// beats ago was still being pulled into ensembles as if standing there.
//
// The scan runs oldest→newest over the recent window and keeps a tiny state
// machine per character:
//   • a DEPARTURE clause about them (theirs or anyone's narration) → departed
//   • any LATER message they speak in → present again (they came back)…
//     …unless that same message itself ends with them leaving
//   • an ENTRY clause about them ("walks back in", "returns") → present
//
// Exclusion applies to AMBIENT/ensemble selection only. Naming, pinning or
// summoning a departed character is an explicit invitation and still works —
// people can be called back into the room.

// Departure verbs demand either a location-ish object or a clause end, so
// "Cruz leaves the cup on the table" can never read as an exit.
const DEPART_RE = (first) => new RegExp(
  `\\b${first}\\b[^.!?\\n]{0,80}?\\b(?:` +
  `(?:leaves|exits|left)\\s+(?:the\\s+)?(?:room|house|kitchen|cabin|lodge|party|hall|building|bar|diner|clearing|den|office|apartment|pack\\s?house)` +
  `|(?:walks?|storms?|stalks?|strides?|slips?|steps?|heads?|marches|hurries|rushes)\\s+(?:out|off|away|outside|home|upstairs|out\\s+the\\s+door)` +
  `|is\\s+gone|leaves\\s*[.!?]|left\\s*[.!?]|departs|disappears\\s+(?:down|into|through|out)` +
  `)`, 'i');
const ENTER_RE = (first) => new RegExp(
  `\\b${first}\\b[^.!?\\n]{0,80}?\\b(?:returns?|comes?\\s+back|walks?\\s+(?:back\\s+)?in|steps?\\s+(?:back\\s+)?(?:in|inside)|re-?enters?|joins|appears\\s+in)\\b` +
  `|\\b(?:returns?|comes?\\s+back|walks?\\s+(?:back\\s+)?in)\\b[^.!?\\n]{0,40}?\\b${first}\\b`, 'i');

/**
 * Ids of characters the recent window shows as having LEFT the scene.
 * @param {Array} messages  recent messages, oldest→newest (scene-scoped by caller)
 * @param {Array} chars     candidate characters [{id, name}]
 * @param {number} window   how many trailing messages to scan
 */
export function detectDepartedCharacterIds(messages, chars, window = 15) {
  const scan = (messages || []).slice(-window);
  const departed = new Set();
  const firstOf = (c) => String(c.name || '').trim().split(/\s+/)[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  for (const m of scan) {
    const text = String(m?.content || m?.text || '');
    for (const c of chars) {
      if (!c?.id || !c?.name) continue;
      // Speaking again = back in the room (unless this very message is their exit).
      if (m.is_ai_character && m.character_id === c.id) departed.delete(c.id);
      const first = firstOf(c);
      if (first.length < 3) continue;
      if (ENTER_RE(first).test(text)) departed.delete(c.id);
      if (DEPART_RE(first).test(text)) departed.add(c.id);
      // Their own reply narrating an unnamed exit ("He turns and walks out.")
      if (m.is_ai_character && m.character_id === c.id
        && /\b(?:she|he|they)\s+[^.!?\n]{0,60}?\b(?:walks?|storms?|slips?|steps?|heads?)\s+(?:out|off|away|outside|home)\b[^.!?\n]{0,40}[.!?]\s*$/i.test(text.trim())) {
        departed.add(c.id);
      }
    }
  }
  return departed;
}
